import { FormGroup } from '@angular/forms';
import { getControl } from '@shared/helpers/form.helper';
import { debounceTime, merge, startWith } from 'rxjs';
import { Utilities } from '@core/helpers/utilities';
import { toNum } from '@shared/helpers/math.helper';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { cloneDeep } from 'lodash-es';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { getBaseSocialeControl } from '../validators/helpers/base-sociale-form.helper';

export const BaseSocialeCalcoliListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    manageTotLavoratoriSoci(form, store);
    manageTotLavoratoriNonSoci(form, store);
    manageTotSociPersoneFisiche(form, store);
    manageTotSociPerSessoEta(form, store);
    manageTotSociSessoProvenienza(form, store);
};

const manageTotLavoratoriSoci = (form: FormGroup, store: Store<Revisione53State>): void => {
    const sMLavComunitari = getBaseSocialeControl(form, 'my_NumSociMLavoratoriComunitari');
    const sFLavComunitari = getBaseSocialeControl(form, 'my_NumSociFLavoratoriComunitari');
    const sMLavExtracomunitari = getBaseSocialeControl(form, 'my_NumSociMLavoratoriExtracomunitari');
    const sFLavExtracomunitari = getBaseSocialeControl(form, 'my_NumSociFLavoratoriExtracomunitari');
    const totControl = getControl(form, 'my_TotLavoratoriSoci');

    merge(
        sMLavComunitari.valueChanges.pipe(startWith(sMLavComunitari.value)),
        sFLavComunitari.valueChanges.pipe(startWith(sFLavComunitari.value)),
        sMLavExtracomunitari.valueChanges.pipe(startWith(sMLavExtracomunitari.value)),
        sFLavExtracomunitari.valueChanges.pipe(startWith(sFLavExtracomunitari.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const totale = toNum(sMLavComunitari.value) + toNum(sFLavComunitari.value) + toNum(sMLavExtracomunitari.value) + toNum(sFLavExtracomunitari.value);

            totControl.setValue(totale.toString(10), { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone = cloneDeep(myVerbale);
                myVerbaleClone.my_TotLavoratoriSoci = totale.toString(10);
                return myVerbaleClone;
            });
        });
};

const manageTotLavoratoriNonSoci = (form: FormGroup, store: Store<Revisione53State>): void => {
    const nsMLavComunitari = getBaseSocialeControl(form, 'my_NumNonSociMLavoratoriComunitari');
    const nsFLavComunitari = getBaseSocialeControl(form, 'my_NumNonSociFLavoratoriComunitari');
    const nsMLavExtracomunitari = getBaseSocialeControl(form, 'my_NumNonSociMLavoratoriExtracomunitari');
    const nsFLavExtracomunitari = getBaseSocialeControl(form, 'my_NumNonSociFLavoratoriExtracomunitari');
    const totControl = getControl(form, 'my_TotLavoratoriNonSoci');

    merge(
        nsMLavComunitari.valueChanges.pipe(startWith(nsMLavComunitari.value)),
        nsFLavComunitari.valueChanges.pipe(startWith(nsFLavComunitari.value)),
        nsMLavExtracomunitari.valueChanges.pipe(startWith(nsMLavExtracomunitari.value)),
        nsFLavExtracomunitari.valueChanges.pipe(startWith(nsFLavExtracomunitari.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const totale = toNum(nsMLavComunitari.value) + toNum(nsFLavComunitari.value) + toNum(nsMLavExtracomunitari.value) + toNum(nsFLavExtracomunitari.value);

            totControl.setValue(totale.toString(10), { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone = cloneDeep(myVerbale);
                myVerbaleClone.my_TotLavoratoriNonSoci = totale.toString(10);
                return myVerbaleClone;
            });
        });
};

const manageTotSociPersoneFisiche = (form: FormGroup, store: Store<Revisione53State>): void => {
    const sociFisCoopDataControl = getBaseSocialeControl(form, 'my_SociFisCoopData');
    const sociFisFinDataControl = getBaseSocialeControl(form, 'my_SociFisFinData');
    const sociSpecDataPersoneFisicheControl = getBaseSocialeControl(form, 'my_gr0001.my_SociSpecData_personeFisiche');
    const totSociPersoneFisicheControl = getControl(form, 'my_TotSociPersoneFisiche');

    merge(
        sociFisCoopDataControl.valueChanges.pipe(startWith(sociFisCoopDataControl.value)),
        sociFisFinDataControl.valueChanges.pipe(startWith(sociFisFinDataControl.value)),
        sociSpecDataPersoneFisicheControl.valueChanges.pipe(startWith(sociSpecDataPersoneFisicheControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const sociSpecPersoneFisiche = sociSpecDataPersoneFisicheControl.value;

            const totale = toNum(sociFisCoopDataControl.value) + toNum(sociFisFinDataControl.value) + (!isNilOrEmpty(sociSpecPersoneFisiche) ? toNum(sociSpecPersoneFisiche) : 0);

            const value = totale.toString(10);

            totSociPersoneFisicheControl.setValue(value, { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone = cloneDeep(myVerbale);

                myVerbaleClone.my_TotSociPersoneFisiche = value;

                return myVerbaleClone;
            });
        });
};

const manageTotSociPerSessoEta = (form: FormGroup, store: Store<Revisione53State>): void => {
    const numMPrimaControl = getBaseSocialeControl(form, 'my_NumMprima');
    const numMSecondaControl = getBaseSocialeControl(form, 'my_NumMseconda');
    const numMTerzaControl = getBaseSocialeControl(form, 'my_NumMterza');
    const numFPrimaControl = getBaseSocialeControl(form, 'my_NumFprima');
    const numFSecondaControl = getBaseSocialeControl(form, 'my_NumFseconda');
    const numFTerzaControl = getBaseSocialeControl(form, 'my_NumFterza');
    const totSociPerSessoEtaControl = getControl(form, 'my_TotSociPerSessoEta');

    merge(
        numMPrimaControl.valueChanges.pipe(startWith(numMPrimaControl.value)),
        numMSecondaControl.valueChanges.pipe(startWith(numMSecondaControl.value)),
        numMTerzaControl.valueChanges.pipe(startWith(numMTerzaControl.value)),
        numFPrimaControl.valueChanges.pipe(startWith(numFPrimaControl.value)),
        numFSecondaControl.valueChanges.pipe(startWith(numFSecondaControl.value)),
        numFTerzaControl.valueChanges.pipe(startWith(numFTerzaControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const totale =
                toNum(numMPrimaControl.value) +
                toNum(numMSecondaControl.value) +
                toNum(numMTerzaControl.value) +
                toNum(numFPrimaControl.value) +
                toNum(numFSecondaControl.value) +
                toNum(numFTerzaControl.value);

            const value = totale.toString(10);

            totSociPerSessoEtaControl.setValue(value, { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone = cloneDeep(myVerbale);

                myVerbaleClone.my_TotSociPerSessoEta = value;

                return myVerbaleClone;
            });
        });
};

const manageTotSociSessoProvenienza = (form: FormGroup, store: Store<Revisione53State>): void => {
    const numSociMComunitariControl = getBaseSocialeControl(form, 'my_NumSociMComunitari');
    const numSociFComunitariControl = getBaseSocialeControl(form, 'my_NumSociFComunitari');
    const numSociMExtracomunitariControl = getBaseSocialeControl(form, 'my_NumSociMExtracomunitari');
    const numSociFExtracomunitariControl = getBaseSocialeControl(form, 'my_NumSociFExtracomunitari');
    const totSociSessoProvenienzaControl = getControl(form, 'my_TotSociSessoProvenienza');

    merge(
        numSociMComunitariControl.valueChanges.pipe(startWith(numSociMComunitariControl.value)),
        numSociFComunitariControl.valueChanges.pipe(startWith(numSociFComunitariControl.value)),
        numSociMExtracomunitariControl.valueChanges.pipe(startWith(numSociMExtracomunitariControl.value)),
        numSociFExtracomunitariControl.valueChanges.pipe(startWith(numSociFExtracomunitariControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const totale =
                toNum(numSociMComunitariControl.value) +
                toNum(numSociFComunitariControl.value) +
                toNum(numSociMExtracomunitariControl.value) +
                toNum(numSociFExtracomunitariControl.value);

            const value = totale.toString(10);

            totSociSessoProvenienzaControl.setValue(value, { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone = cloneDeep(myVerbale);

                myVerbaleClone.my_TotSociSessoProvenienza = value;

                return myVerbaleClone;
            });
        });
};
